/**
 * Type definitions for the Visual Decision Editor
 *
 * UI-layer types that wrap the canonical DecisionDefinition.
 * The canonical schema types are re-exported from decision-definition-schema.ts.
 */

export type {
  AstExpression,
  DecisionDefinition,
  DecisionEditorState,
  DecisionCapabilityDocument,
  DecisionEvaluationResult,
  RuleElement,
  EmitRule,
  EmitPayload,
  Obligation,
  DecisionFlag,
  DecisionReason,
  NodeEvaluationTrace,
  EditorNodeState,
  ScoreBand,
  ComparisonOperator,
  LogicalOperator,
  ValueType,
  MissingValuePolicy,
  TriState,
  DecisionMode,
  Severity,
  JsonValue,
  LegislationReference,
  ExplainTemplates,
  EvidenceRequirements,
} from "./decision-definition-schema";

export {
  AstExpressionSchema,
  DecisionDefinitionSchema,
  DecisionEditorStateSchema,
  DecisionCapabilityDocumentSchema,
  DecisionEvaluationResultSchema,
  RuleElementSchema,
  EmitRuleSchema,
  EmitPayloadSchema,
  ObligationSchema,
  DecisionFlagSchema,
  DecisionReasonSchema,
  V1_NODE_TYPES,
  validateDecisionDefinition,
  generateNodeId,
  createLiteralExpression,
  createFactRefExpression,
  createComparisonExpression,
  createLogicalExpression,
  createEmptyDefinition,
  createEmptyDocument,
} from "./decision-definition-schema";

import type {
  DecisionDefinition,
  RuleElement,
  AstExpression,
} from "./decision-definition-schema";

// =============================================================================
// Visual editor wrapper types (UI-only concerns)
// =============================================================================

/**
 * VisualDecision wraps the canonical DecisionDefinition with UI-only state:
 * which elements are expanded, which tab is active, etc.
 */
export interface VisualDecision {
  definition: DecisionDefinition;
  expandedElements: Set<string>;
  activeTab: "rule-elements" | "table" | "outputs" | "test";
  showJsonPreview: boolean;
}

export function createVisualDecision(
  definition: DecisionDefinition
): VisualDecision {
  return {
    definition,
    expandedElements: new Set(definition.ruleElements.map((r) => r.id)),
    activeTab: "rule-elements",
    showJsonPreview: false,
  };
}

/**
 * Visual rule element — extends canonical with UI-specific state
 */
export interface VisualRuleElement extends RuleElement {
  isExpanded: boolean;
  editorMode: "graph" | "form" | "table";
}

export function createVisualRuleElement(
  ruleElement: RuleElement
): VisualRuleElement {
  return {
    ...ruleElement,
    isExpanded: true,
    editorMode: "form",
  };
}

// =============================================================================
// Case context fields (for field pickers and test UI)
// =============================================================================

export interface CaseContextField {
  path: string;
  label: string;
  type: "string" | "number" | "boolean" | "date" | "array" | "object";
  description?: string;
  required?: boolean;
}

/**
 * Extract case context fields from the decision definition by walking the AST
 * and collecting fact_ref / field nodes.
 */
export function extractCaseContextFields(
  definition: DecisionDefinition
): CaseContextField[] {
  const fields = new Map<string, CaseContextField>();

  function walk(expr: AstExpression | undefined) {
    if (!expr) return;

    if (
      (expr.type === "fact_ref" || expr.type === "field" || expr.type === "context_ref") &&
      expr.path
    ) {
      if (!fields.has(expr.path)) {
        fields.set(expr.path, {
          path: expr.path,
          label: expr.path.split(".").pop() || expr.path,
          type: mapValueType(expr.valueType),
          required: expr.required,
        });
      }
    }

    if (expr.left) walk(expr.left);
    if (expr.right) walk(expr.right);
    if (expr.children) expr.children.forEach(walk);
    if (expr.target) walk(expr.target);
    if (expr.operand) walk(expr.operand);
    if (expr.set) expr.set.forEach(walk);
    if (expr.arguments) expr.arguments.forEach(walk);
    if (expr.cases) {
      expr.cases.forEach((c) => {
        walk(c.when);
        walk(c.then);
      });
    }
    if (expr.factors) {
      expr.factors.forEach((f) => walk(f.when));
    }
    if (expr.else) walk(expr.else);
  }

  walk(definition.root);
  for (const re of definition.ruleElements) {
    walk(re.expression);
  }

  return Array.from(fields.values());
}

function mapValueType(
  vt?: string
): "string" | "number" | "boolean" | "date" | "array" | "object" {
  switch (vt) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "date":
    case "datetime":
      return "date";
    case "array":
      return "array";
    case "object":
      return "object";
    default:
      return "string";
  }
}

// =============================================================================
// Templates and validation (UI-level)
// =============================================================================

export interface DecisionTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  definition: Partial<DecisionDefinition>;
  exampleCaseContext?: Record<string, unknown>;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
}

// =============================================================================
// Composition types (legacy support, kept for backward compat)
// =============================================================================

export interface VisualComposition {
  strategy: "any_fail" | "all_pass" | "custom";
  rules: GroundRule[];
}

export interface GroundRule {
  id: string;
  condition: FindingCondition;
  ground: VisualGround;
}

export type FindingCondition =
  | FindingConditionSimple
  | FindingConditionBinary
  | FindingConditionComparison;

export interface FindingConditionSimple {
  type: "finding";
  elementId: string;
  outcome: "YES" | "NO" | "UNKNOWN";
}

export interface FindingConditionBinary {
  type: "binary";
  operator: "AND" | "OR";
  left: FindingCondition;
  right: FindingCondition;
}

export interface FindingConditionComparison {
  type: "comparison";
  operator: "==" | "!=" | "<" | "<=" | ">" | ">=";
  left: FindingFieldReference;
  right: LiteralValue | FindingFieldReference;
}

export interface FindingFieldReference {
  type: "finding_field";
  elementId: string;
  field: "outcome" | "confidence" | "explanation";
}

export interface LiteralValue {
  type: "literal";
  value: string | number | boolean;
}

export interface VisualGround {
  key: string;
  sectionRef: string;
  label: string;
  draftingSnippetTemplate?: string;
}

export interface VisualScoring {
  strengthRules: StrengthRule[];
  defaultStrength: "High" | "Medium" | "Low";
}

export interface StrengthRule {
  id: string;
  condition: FindingConditionComparison;
  strength: "High" | "Medium" | "Low";
}

export interface DraftingSnippet {
  id: string;
  key: string;
  template: string;
  variables?: string[];
}
